import { useTranslation } from '../../../shared/i18n';

import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Button,
    Space,
    Tag,
    Tooltip,
    Modal,
    Form,
    Input,
    message,
    Typography,
    Row,
    Col,
    Statistic,
    Alert,
    Steps,
    Spin,
    Empty,
    Result,
    theme
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    GlobalOutlined,
    SearchOutlined,
    InfoCircleOutlined,
    RocketOutlined,
    WarningOutlined,
    CopyOutlined,
    SafetyCertificateOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dnsService from '../../services/dnsService';
import { billingService } from '../../services/billingService';
import DnsDnssec from './DnsDnssec';
import { validateDomainRule } from '../../../shared/utils/validation';
import { dnsZoneStatusConfig } from '../../../shared/components/DnsZoneStatusTag';

import { userPath } from '../../../shared/config/paths';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';
import { formatDateTime } from '../../../shared/utils/date';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';
import RenewAction from '../../../shared/components/RenewAction';
import { Pick, StepHead } from '../../../shared/components/WizardParts';
import RowActions, { useRowContextMenu } from '../../../shared/components/RowActions';
import { showOwnershipRequired } from '../../../shared/components/OwnershipTxt';

const { Text } = Typography;
const { confirm } = Modal;

const DnsZones = () => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const [zones, setZones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingZone, setEditingZone] = useState(null);
    const [form] = Form.useForm();
    const [searchValue, setSearchValue] = useState('');
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalZones: 0,
        activeZones: 0,
        totalRecords: 0
    });
    const [sortInfo, setSortInfo] = useState({ field: '', order: '' });
    const [subscription, setSubscription] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [modalSubmitting, setModalSubmitting] = useState(false);
    const [createdZone, setCreatedZone] = useState(null);
    const [pendingInvoiceId, setPendingInvoiceId] = useState(null);
    const [selectedSubscriptionStatus, setSelectedSubscriptionStatus] = useState(null);
    const [paymentChecking, setPaymentChecking] = useState(false);
    const [dnssecZone, setDnssecZone] = useState(null);
    const [dnsProducts, setDnsProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [pendingZoneInfo, setPendingZoneInfo] = useState(null); // 保存step0填写的域名信息
    const [unboundSubs, setUnboundSubs] = useState([]);       // 已购未使用的套餐
    const [selectedExistingSub, setSelectedExistingSub] = useState(null);
    const location = useLocation();

    const fetchUnboundSubs = async () => {
        const r = await dnsService.getDnsSubscriptions({ unbound: 1 });
        const list = Array.isArray(r.data) ? r.data : [];
        setUnboundSubs(list);
        return list;
    };

    // /dns/zones?add=<域名>：从产品页「托管到 Yeap DNS」进来，打开向导并预填域名
    useEffect(() => {
        const qs = new URLSearchParams(location.search);
        // ?new=1 (dashboard / onboarding): just open the add-zone wizard
        if (qs.get('new') === '1') { showCreateModal(); return; }
        const add = (qs.get('add') || '').trim().toLowerCase();
        if (!add || !/^[a-z0-9.-]+\.[a-z0-9-]{2,}$/.test(add)) return;
        showCreateModal();
        setTimeout(() => form.setFieldsValue({ zone: add }), 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 颜色映射
    const planColors =['#8c8c8c', '#1890ff', '#52c41a', '#faad14'];
    const planTags = [t('Starter'), t('Recommended'), t('Advanced'), t('Business')];

    const fetchDnsProducts = async () => {
        setProductsLoading(true);
        try {
            const result = await dnsService.getDnsProducts();
            if (result.success && result.data) {
                // 转换为前端需要的格式
                const products = (Array.isArray(result.data) ? result.data : []).map((p, idx) => ({
                    id: p.id,
                    name: p.name,
                    price: p.monthly_price > 0 ? t('{{value}}/month', { value: formatMoney(p.monthly_price, p.currency || p.pricing?.[0]?.currency || PLATFORM_CURRENCY) }) : t('Free'),
                    priceValue: p.monthly_price || 0,
                    color: planColors[idx % planColors.length],
                    tag: planTags[idx % planTags.length],
                    features: p.features || [],
                    description: p.description
                }));
                setDnsProducts(products);
            }
        } catch (error) {
            console.error('获取DNS产品失败:', error);
        } finally {
            setProductsLoading(false);
        }
    };

    const fetchZones = async (sort = sortInfo) => {
        setLoading(true);
        try {
            const params = {
                search: searchValue
            };
            if (sort.field) {
                params.sortField = sort.field;
                params.sortOrder = sort.order;
            }
            const result = await dnsService.getDnsZones(params);
            if (result.success) {
                const data = Array.isArray(result.data) ? result.data : [];
                setZones(data);

                // 计算统计信息
                const activeZones = data.filter(zone => zone.status === 'active').length;
                const totalRecords = data.reduce((sum, zone) => sum + (zone.records_count || 0), 0);

                setStats({
                    totalZones: data.length,
                    activeZones,
                    totalRecords
                });
            } else {
                message.error(result.error || t('Failed to retrieve DNS zones'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve DNS zones: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchZones();
        fetchSubscriptionInfo();
    }, [searchValue]);

    useEffect(() => {
        if (!editingZone) {
            setSelectedSubscriptionId(null);
            setSelectedPlanId(null);
        }
        // 打开创建模态框时获取产品列表
        if (!editingZone && isModalVisible && dnsProducts.length === 0) {
            fetchDnsProducts();
        }
        if (!editingZone && isModalVisible && currentStep === 1) {
            fetchUnboundSubs();
        }
    }, [currentStep, editingZone, isModalVisible]);

    const fetchSubscriptionInfo = async () => {
        try {
            const response = await billingService.getSubscriptionOverview('dns');
            if (response.success) {
                setSubscription(response.data);
                if (response.data.subscription_id) {
                    setSelectedSubscriptionId(response.data.subscription_id);
                }
                if (response.data.status) {
                    setSelectedSubscriptionStatus(response.data.status);
                }
            }
        } catch (error) {
            console.error('获取套餐信息失败：', error);
        }
    };

    const showCreateModal = () => {
        // 直接打开wizard，在wizard中选择套餐
        form.resetFields();
        setEditingZone(null);
        setSelectedPlanId(null);
        setSelectedSubscriptionId(null);
        setSelectedSubscriptionStatus(null);
        setPendingInvoiceId(null);
        setPendingZoneInfo(null);
        setSelectedExistingSub(null);
        setCurrentStep(0);
        setCreatedZone(null);
        setIsModalVisible(true);
    };

    const showEditModal = (zone) => {
        setEditingZone(zone);
        form.setFieldsValue({
            zone: zone.zone,
            description: zone.description || '',
            status: zone.status
        });
        setCurrentStep(0);
        setCreatedZone(null);
        setSelectedPlanId(null);
        setSelectedSubscriptionId(zone.subscription_id || null);
        setSelectedSubscriptionStatus(zone.subscription_status || null);
        setPendingInvoiceId(null);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingZone(null);
        setCurrentStep(0);
        setCreatedZone(null);
        setModalSubmitting(false);
        setPendingInvoiceId(null);
        setPendingZoneInfo(null);
        setSelectedSubscriptionStatus(null);
        form.resetFields();
    };

    const resolvePlan = () => {
        if (selectedPlanId) {
            return dnsProducts.find((p) => p.id === selectedPlanId);
        }
        if (subscription?.product_id) {
            return dnsProducts.find((p) => p.id === subscription.product_id);
        }
        return null;
    };

    const refreshBillingStatus = async () => {
        const plan = resolvePlan();
        if (plan && plan.priceValue === 0) {
            setSelectedSubscriptionStatus('active');
            return 'active';
        }

        if (!selectedSubscriptionId) {
            message.warning(t('Please create a plan first'));
            return null;
        }

        setPaymentChecking(true);
        try {
            if (pendingInvoiceId) {
                const invoiceResp = await billingService.getInvoice(pendingInvoiceId);
                if (invoiceResp.success && invoiceResp.data?.status === 'paid') {
                    setSelectedSubscriptionStatus('processing');
                }
            }

            const resp = await billingService.getSubscription(selectedSubscriptionId);
            if (resp.success && resp.data) {
                setSelectedSubscriptionStatus(resp.data.status);
                if (!selectedPlanId && resp.data.product_id) {
                    setSelectedPlanId(resp.data.product_id);
                }
                return resp.data.status;
            }
        } catch (error) {
            message.error(error.error || error.message || t('Failed to refresh payment status'));
        } finally {
            setPaymentChecking(false);
        }
        return null;
    };

    // Step 0 → Step 1: 验证域名表单，保存信息，进入套餐选择
    const handleDomainNextStep = async () => {
        try {
            const values = await form.validateFields();
            // 后端前置校验（保留域/他人已托管/父子域重叠），失败则不进入下一步
            const pre = await dnsService.precheckDnsZone({ zone: values.zone });
            if (!pre.success) {
                if (!showOwnershipRequired(pre)) message.error(pre.error || t('This domain cannot be added'));
                return;
            }
            // 保存域名信息到状态
            setPendingZoneInfo({
                zone: values.zone,
                description: values.description || ''
            });
            // 确保有产品数据
            if (dnsProducts.length === 0 && !productsLoading) {
                fetchDnsProducts();
            }
            setCurrentStep(1);
        } catch (error) {
            // 表单验证失败，不需要额外处理
        }
    };

    // Step 1 → Step 2: 创建订阅和区域
    const handleCreateZone = async () => {
        try {
            // 使用保存的域名信息
            if (!pendingZoneInfo || !pendingZoneInfo.zone) {
                message.error(t('Domain information is missing. Please go back and enter it again.'));
                return;
            }

            if (selectedExistingSub) {
                setModalSubmitting(true);
                const pre0 = await dnsService.precheckDnsZone({ zone: pendingZoneInfo.zone, product_id: selectedExistingSub.product_id });
                if (!pre0.success) {
                    if (!showOwnershipRequired(pre0)) message.error(pre0.error || t('This domain cannot be added'));
                    setModalSubmitting(false);
                    return;
                }
                const r0 = await dnsService.createDnsZone({
                    zone: pendingZoneInfo.zone,
                    description: pendingZoneInfo.description,
                    subscription_id: selectedExistingSub.id,
                });
                setModalSubmitting(false);
                if (!r0.success) {
                    if (!showOwnershipRequired(r0)) message.error(r0.error || t('Failed to create DNS zone'));
                    return;
                }
                message.success(t('Zone created successfully'));
                setSelectedSubscriptionId(selectedExistingSub.id);
                setSelectedSubscriptionStatus('active');
                setCreatedZone({
                    id: r0.data?.id,
                    zone: r0.data?.zone || pendingZoneInfo.zone,
                    display_zone: r0.data?.display_zone || pendingZoneInfo.zone,
                    ns: Array.isArray(r0.data?.ns) ? r0.data.ns : [],
                    description: pendingZoneInfo.description,
                });
                setCurrentStep(2);
                fetchZones();
                return;
            }

            const plan = resolvePlan();
            if (!plan) {
                message.error(t('Please select a plan first'));
                return;
            }

            setModalSubmitting(true);

            const isFree = plan.priceValue === 0;

            // 创建订阅/发票前再校验一次（含该套餐每账号区域数上限），避免产生用不了的订阅
            const pre = await dnsService.precheckDnsZone({ zone: pendingZoneInfo.zone, product_id: plan.id });
            if (!pre.success) {
                if (!showOwnershipRequired(pre)) message.error(pre.error || t('This domain cannot be added'));
                setModalSubmitting(false);
                return;
            }

            // 创建 DNS 区域 + 套餐（后端校验通过后一起建，订阅从诞生起就挂在该区域上）
            const result = await dnsService.createDnsZone({
                zone: pendingZoneInfo.zone,
                description: pendingZoneInfo.description,
                product_id: plan.id,
                billing_cycle: isFree ? 'onetime' : 'monthly',
            });

            if (result.success) {
                setSelectedSubscriptionId(result.data?.subscription_id || null);
                setPendingInvoiceId(result.data?.invoice_id || null);
                setSelectedSubscriptionStatus(isFree ? 'active' : 'pending');
                message.success(t('Zone created successfully'));
                setCreatedZone({
                    id: result.data?.id,
                    zone: result.data?.zone || pendingZoneInfo.zone,
                    display_zone: result.data?.display_zone || pendingZoneInfo.zone, // IDN显示原始Unicode
                    ns: Array.isArray(result.data?.ns) ? result.data.ns : [], // 分配给该区域的 Nameserver 主机名
                    description: pendingZoneInfo.description
                });

                // 免费套餐直接激活，显示NS配置步骤
                if (isFree) {
                    setCurrentStep(2);
                } else {
                    // 付费套餐：提示支付
                    Modal.confirm({
                        title: t('Zone created. Please complete payment.'),
                        content: t('Paid plans require payment before use. You can pay now or later on the Invoices page.'),
                        okText: t('Pay now'),
                        cancelText: t('Later'),
                        onOk: () => {
                            handleModalClose();
                            navigate(userPath(`/billing/invoices?id=${result.data?.invoice_id}`));
                        },
                        onCancel: () => {
                            setCurrentStep(2);
                        }
                    });
                }
                fetchZones();
            } else if (!showOwnershipRequired(result)) {
                message.error(result.error || t('Failed to create DNS zone'));
            }
        } catch (error) {
            message.error(t('Operation failed: {{value}}', { value: error.message || t('Unknown error') }));
        } finally {
            setModalSubmitting(false);
        }
    };

    const handleEditSave = async () => {
        try {
            const values = await form.validateFields();
            setModalSubmitting(true);
            const result = await dnsService.updateDnsZone({
                id: editingZone.id,
                ...values
            });

            if (result.success) {
                message.success(t('Zone updated successfully'));
                handleModalClose();
                fetchZones();
            } else {
                message.error(result.error || t('Failed to update DNS zone'));
            }
        } catch (error) {
            if (!error.errorFields) {
                message.error(t('Operation failed: {{message}}', { message: error.message }));
            }
        } finally {
            setModalSubmitting(false);
        }
    };

    const handleGoToRecords = () => {
        if (!createdZone?.id) {
            message.warning(t('Zone creation is not yet complete'));
            return;
        }
        const plan = selectedExistingSub ? null : resolvePlan();
        if (plan && plan.priceValue > 0 && !['active', 'processing'].includes(selectedSubscriptionStatus)) {
            Modal.confirm({
                title: t('Plan payment incomplete'),
                icon: <WarningOutlined />,
                content: t('Please complete payment for the current plan before adding more DNS records.'),
                okText: t('Pay now'),
                cancelText: t('Later'),
                onOk: () => {
                    if (pendingInvoiceId) {
                        navigate(userPath(`/billing/invoices?id=${pendingInvoiceId}`));
                    } else {
                        navigate(userPath('/billing/invoices'));
                    }
                }
            });
            return;
        }
        handleModalClose();
        navigate(userPath(`/dns/records/${createdZone.id}`), { state: { zoneName: createdZone.display_zone || createdZone.zone } });
    };

    const copyToClipboard = (text) => {
        if (!text) {
            return;
        }

        navigator.clipboard.writeText(text).then(() => {
            message.success(t('Copied to clipboard'));
        }).catch(() => {
            message.error(t('Failed to copy. Please copy manually.'));
        });
    };

    const renderStepContent = () => {
        // 编辑模式：只显示域名表单
        if (editingZone) {
            return (
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="zone"
                        label={t('Zone name')}
                        rules={[
                            { required: true, message: t('Please enter a zone name') },
                            { validator: validateDomainRule }
                        ]}
                    >
                        <Input placeholder="example.com" disabled />
                    </Form.Item>
                    <Form.Item name="description" label={t('Description')}>
                        <Input.TextArea rows={3} placeholder={t('Optional zone description')} />
                    </Form.Item>
                </Form>
            );
        }

        // 创建模式 Step 0: 输入域名
        if (currentStep === 0) {
            return (
                <Form form={form} layout="vertical">
                    <StepHead title={t('Which domain do you want to host?')} desc={t('Enter the domain you registered, e.g. example.com.')} />
                    <Form.Item
                        name="zone"
                        rules={[
                            { required: true, message: t('Please enter a zone name') },
                            { validator: validateDomainRule }
                        ]}
                    >
                        <Input size="large" prefix={<GlobalOutlined style={{ color: token.colorTextTertiary }} />}
                            placeholder={t('example.com or 中国.com')} onPressEnter={handleDomainNextStep}
                            style={{ fontSize: 18, padding: '10px 14px' }} />
                    </Form.Item>
                    <Form.Item name="description" label={t('Description')}>
                        <Input placeholder={t('Optional zone description')} />
                    </Form.Item>
                </Form>
            );
        }

        // 创建模式 Step 1: 选择套餐
        if (currentStep === 1) {
            const picked = selectedPlanId ? resolvePlan() : null;
            return (
                <div>
                    <StepHead
                        title={t('Choose a plan for {{zone}}', { zone: pendingZoneInfo?.zone || '' })}
                        desc={t('Free plans activate immediately; paid plans activate after payment.')}
                    />
                    {unboundSubs.length > 0 && (
                        <>
                            <Text strong style={{ display: 'block', marginBottom: 12 }}>{t('Bind to an existing plan')}</Text>
                            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                                {unboundSubs.map((s) => (
                                    <Col xs={24} sm={12} key={s.id}>
                                        <Pick
                                            active={selectedExistingSub?.id === s.id}
                                            onClick={() => { setSelectedExistingSub(s); setSelectedPlanId(null); }}
                                            title={s.name || `#${s.id}`}
                                            desc={s.shared
                                                ? `${s.product_name} · ${t('Bound {{used}}/{{limit}}', { used: s.used, limit: s.limit < 0 ? t('Unlimited') : s.limit })}`
                                                : s.product_name}
                                        />
                                    </Col>
                                ))}
                            </Row>
                            <Text strong style={{ display: 'block', marginBottom: 12 }}>{t('Buy a new plan')}</Text>
                        </>
                    )}
                    {productsLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin size="large" /></div>
                    ) : dnsProducts.length === 0 ? (
                        <Alert
                            message={t('No plans available')}
                            description={t('Please contact an administrator to configure a DNS plan before creating a domain.')}
                            type="warning"
                            showIcon
                        />
                    ) : (
                        <Row gutter={[16, 16]}>
                            {dnsProducts.map((plan) => (
                                <Col xs={24} sm={12} key={plan.id}>
                                    <Pick
                                        active={selectedPlanId === plan.id}
                                        onClick={() => { setSelectedPlanId(plan.id); setSelectedExistingSub(null); }}
                                        title={<>{plan.name}{plan.tag && <Tag color={plan.color} style={{ marginLeft: 8 }}>{plan.tag}</Tag>}</>}
                                        extra={plan.priceValue === 0 ? t('Free') : plan.price}
                                        desc={plan.description}
                                    >
                                        {plan.features?.length > 0 && (
                                            <ul style={{ margin: '10px 0 0', paddingLeft: 18, color: token.colorTextSecondary, fontSize: 13, lineHeight: '22px' }}>
                                                {plan.features.map((f, i) => <li key={i}>{f}</li>)}
                                            </ul>
                                        )}
                                    </Pick>
                                </Col>
                            ))}
                        </Row>
                    )}
                    {picked && picked.priceValue > 0 && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>{t('Paid plans require payment after creation to activate.')}</Text>
                    )}
                </div>
            );
        }

        // 创建模式 Step 2: 完成，显示NS
        const nsList = createdZone?.ns || [];
        return (
            <div>
                <Result
                    status="success"
                    style={{ padding: '8px 0 16px' }}
                    title={t('{{zone}} added', { zone: createdZone?.display_zone || createdZone?.zone || '' })}
                    subTitle={t('Set these nameservers at your domain registrar to finish. It usually takes effect within an hour.')}
                />
                {nsList.length > 0 ? (
                    <div style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: token.borderRadiusLG, overflow: 'hidden' }}>
                        {nsList.map((ns, i) => (
                            <div key={ns} style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
                                borderTop: i ? `1px solid ${token.colorBorderSecondary}` : 'none',
                            }}>
                                <Text strong style={{ fontFamily: 'monospace', fontSize: 15 }}>{ns}</Text>
                                <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(ns)}>{t('Copy')}</Button>
                            </div>
                        ))}
                        <div style={{ padding: '8px 16px', background: token.colorFillQuaternary, textAlign: 'right' }}>
                            <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(nsList.join('\n'))}>{t('Copy all')}</Button>
                        </div>
                    </div>
                ) : (
                    <Alert
                        message={t('No Nameserver assigned yet')}
                        description={t('No nameservers have been assigned to this zone yet. Please contact an administrator to check the DNS node group configuration.')}
                        type="warning"
                        showIcon
                    />
                )}
            </div>
        );
    };

    const renderModalFooter = () => {
        if (editingZone) {
            return [
                <Button key="cancel" onClick={handleModalClose}>
                    {t('Cancel')}
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    loading={modalSubmitting}
                    onClick={handleEditSave}
                >
                    {t('Save')}
                </Button>
            ];
        }

        // Step 0: 域名输入 → 下一步选择套餐
        if (currentStep === 0) {
            return [
                <Button key="cancel" onClick={handleModalClose}>
                    {t('Cancel')}
                </Button>,
                <Button
                    key="next"
                    type="primary"
                    onClick={handleDomainNextStep}
                >
                    {t('Next: Select a plan')}
                </Button>
            ];
        }

        // Step 1: 套餐选择 → 创建域名
        if (currentStep === 1) {
            return [
                <Button key="back" onClick={() => setCurrentStep(0)}>
                    {t('Previous step')}
                </Button>,
                <Button
                    key="create"
                    type="primary"
                    loading={modalSubmitting}
                    disabled={(!selectedPlanId && !selectedExistingSub) || productsLoading}
                    onClick={handleCreateZone}
                >
                    {t('Create domain')}
                </Button>
            ];
        }

        // Step 2: 完成
        return [
            <Button
                key="records"
                type="primary"
                disabled={!createdZone?.id}
                onClick={handleGoToRecords}
            >
                {t('Manage DNS records')}
            </Button>,
            <Button key="finish" onClick={handleModalClose}>
                {t('Done')}
            </Button>
        ];
    };

    const handleDelete = (zone) => {
        confirm({
            title: t('Confirm deletion'),
            icon: <ExclamationCircleOutlined />,
            content: t('Delete zone "{{value}}"? All related records will be deleted. This action cannot be undone.', { value: zone.display_zone || zone.zone }),
            okText: t('Delete'),
            okType: 'danger',
            cancelText: t('Cancel'),
            onOk: async () => {
                try {
                    setLoading(true);
                    const result = await dnsService.deleteDnsZone(zone.id);

                    if (result.success) {
                        message.success(t('Zone deleted successfully'));
                        fetchZones();
                    } else {
                        message.error(result.error || t('Failed to delete DNS zone'));
                    }
                } catch (error) {
                    message.error(t('Failed to delete DNS zone: {{message}}', { message: error.message }));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 手动检查NS状态
    const handleCheckNs = async (zone) => {
        try {
            message.loading({ content: t('Checking NS status...'), key: 'ns-check' });
            const result = await dnsService.checkNsStatus(zone.id);
            message.destroy('ns-check');

            if (result.success) {
                const data = result.data;
                if (data.ns_status === 'active') {
                    message.success(t('NS points to the correct servers. Domain connected!'));
                } else {
                    // 显示详细的NS信息
                    Modal.warning({
                        title: t('NS is not configured correctly yet'),
                        width: 520,
                        content: (
                            <div>
                                <p style={{ marginBottom: 16 }}>{t('Please change the NS records at your domain registrar to the following addresses:')}</p>
                                <div style={{ marginBottom: 16 }}>
                                    <Text strong>{t('Current NS:')}</Text>
                                    <div style={{ background: '#f5f5f5', padding: 8, borderRadius: 4, marginTop: 4 }}>
                                        {data.current_ns?.length > 0
                                            ? data.current_ns.map((ns, i) => <div key={i}><Text code>{ns}</Text></div>)
                                            : <Text type="secondary">{t('No NS records detected')}</Text>
                                        }
                                    </div>
                                </div>
                                <div>
                                    <Text strong>{t('Should point to:')}</Text>
                                    <div style={{ background: '#e6f7ff', padding: 8, borderRadius: 4, marginTop: 4 }}>
                                        {data.expected_ns?.map((ns, i) => (
                                            <div key={i}>
                                                <Text code copyable>{ns}</Text>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )
                    });
                }
                fetchZones(); // 刷新列表
            } else {
                message.error(result.error || t('Failed to check NS status'));
            }
        } catch (error) {
            message.error(t('Failed to check NS status: {{message}}', { message: error.message }));
        }
    };

    // 激活待支付的区域
    const handleActivateZone = async (zone) => {
        if (!zone.subscription_id) {
            message.error(t('This zone has no linked subscription'));
            return;
        }

        try {
            // 获取订阅信息查找待支付的账单
            const subResp = await billingService.getSubscription(zone.subscription_id);
            if (subResp.success && subResp.data) {
                const sub = subResp.data;
                if (sub.status === 'active') {
                    // 订阅已激活，刷新区域状态
                    message.success(t('Subscription activated. Refreshing...'));
                    fetchZones();
                    return;
                }

                // 查找待支付账单
                if (sub.invoice_id) {
                    navigate(userPath(`/billing/invoices?id=${sub.invoice_id}`));
                } else {
                    // 没有找到账单，跳转到账单列表
                    navigate(userPath('/billing/invoices'));
                }
            } else {
                message.error(t('Failed to retrieve subscription information'));
            }
        } catch (error) {
            message.error(t('Operation failed: {{message}}', { message: error.message }));
        }
    };

    // 处理表格排序
    const handleTableChange = (pagination, filters, sorter) => {
        let newSort = { field: '', order: '' };
        if (sorter && sorter.field && sorter.order) {
            newSort = {
                field: sorter.field,
                order: sorter.order === 'ascend' ? 'asc' : 'desc'
            };
        }
        setSortInfo(newSort);
        fetchZones(newSort);
    };

    const getRowActions = (record) => [
        // pending 状态显示激活按钮
        { key: 'activate', label: t('Activate'), icon: <RocketOutlined />, inline: true, primary: true, tooltip: t('Complete payment to activate the zone'), hidden: !(record.status === 'pending' && record.subscription_id), onClick: () => handleActivateZone(record) },
        // NS未接入显示检查按钮
        { key: 'check-ns', label: t('Check NS'), icon: <SyncOutlined />, inline: true, tooltip: t('Check whether NS records point to our servers'), hidden: !(record.status === 'active' && record.ns_status === 'pending'), onClick: () => handleCheckNs(record) },
        { key: 'records', label: t('Records'), icon: <SearchOutlined />, inline: true, tooltip: t('Manage records'), onClick: () => navigate(userPath(`/dns/records/${record.id}`), { state: { zoneName: record.display_zone || record.zone } }) },
        { key: 'dnssec', label: 'DNSSEC', icon: <SafetyCertificateOutlined />, onClick: () => setDnssecZone(record) },
        { key: 'plan', label: record.subscription_id ? t('Plan') : t('Select a plan'), hidden: ['deleted', 'terminated'].includes(record.status), onClick: () => navigate(userPath(record.subscription_id ? `/dns/records/${record.id}?plan=1` : `/dns/plans?zone_id=${record.id}`)) },
        { key: 'edit', label: t('Edit'), icon: <EditOutlined />, onClick: () => showEditModal(record) },
        { key: 'delete', label: t('Delete'), icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(record) },
    ];
    const rowMenu = useRowContextMenu(getRowActions);

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            sorter: true,
        },
        {
            title: t('DNS zones'),
            dataIndex: 'zone',
            key: 'zone',
            sorter: true,
            render: (text, record) => {
                const name = record.product_name;
                // 优先显示 display_zone（Unicode IDN），否则显示 zone（Punycode）
                const displayName = record.display_zone || text;
                let planColor = 'default';
                if (name) {
                    if (name.includes('免费') || name.includes('入门')) planColor = 'default';
                    else if (name.includes('专业') || name.includes('标准')) planColor = 'blue';
                    else if (name.includes('企业')) planColor = 'gold';
                    else if (name.includes('旗舰') || name.includes('高级')) planColor = 'purple';
                }
                return (
                    <Space>
                        <GlobalOutlined />
                        <Tooltip title={text !== displayName ? `Punycode: ${text}` : null}>
                            <Text strong>{displayName}</Text>
                        </Tooltip>
                        {name && <Tag color={planColor}>{name}</Tag>}
                    </Space>
                );
            },
        },
        {
            title: t('Record count'),
            dataIndex: 'records_count',
            key: 'records_count',
            width: 100,
            render: (count) => <Tag color="blue">{count || 0}</Tag>
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status, record) => {
                const statusInfo = dnsZoneStatusConfig(status);
                return (
                    <Space direction="vertical" size={0}>
                        <Tag color={statusInfo.color}>{t(statusInfo.text)}</Tag>
                        {record.ns_migrating ? (
                            <Tooltip title={t('Plan changed. Update the NS at your registrar to the new NS (the old NS still resolve DNS).')}>
                                <Tag color="orange" style={{ fontSize: 10 }}>{t('NS update pending')}</Tag>
                            </Tooltip>
                        ) : record.ns_status === 'pending' && status === 'active' && (
                            <Tag color="orange" style={{ fontSize: 10 }}>{t('Connection pending')}</Tag>
                        )}
                        {record.verified === false && record.verify_deadline && (
                            <span style={{ fontSize: 11, color: '#d46b08' }}>{t('Removed on {{date}} if nameservers are not changed', { date: String(record.verify_deadline).slice(0, 10) })}</span>
                        )}
                    </Space>
                );
            }
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            sorter: true,
            render: (v) => formatDateTime(v),
        },
        {
            title: t('Expires'),
            key: 'billing',
            width: 170,
            render: (_, record) => <RenewAction billing={record.billing} resourceName={record.zone} />,
        },
        {
            title: t('Actions'),
            key: 'action',
            fixed: 'right',
            render: (_, record) => <RowActions actions={getRowActions(record)} />,
        },
    ];

    return (
        <div>

            {/* 统计卡片 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title={t('Total zones')}
                            value={stats.totalZones}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title={t('Active zones')}
                            value={stats.activeZones}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card>
                        <Statistic
                            title={t('Total records')}
                            value={stats.totalRecords}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 区域列表 */}
            <Card
                title={t('DNS zone list')}
                extra={
                    <Space>
                        <Input.Search
                            placeholder={t('Search zones')}
                            allowClear
                            onSearch={setSearchValue}
                            style={{ width: 200 }}
                        />
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={showCreateModal}
                        >
                            {t('Create zone')}
                        </Button>
                    </Space>
                }
            >
                <Table
                    onRow={rowMenu.onRow(rowLink(navigate, (r) => `/dns/records/${r.id}`))}
                    rowClassName={() => ROW_LINK_CLASS}
                    columns={columns}
                    dataSource={zones}
                    rowKey="id"
                    loading={loading}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: <Empty description={t('No DNS zones yet. Click "Create zone" in the top right.')} />
                    }}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: (total) => t('{{total}} records total', { total }),
                    }}
                    showSorterTooltip={false}
                />
            </Card>

            {rowMenu.menu}

            {/* 创建/编辑模态框 */}
            <Modal
                title={editingZone ? t('Edit DNS zone') : t('Create DNS zone')}
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={renderModalFooter()}
                width={editingZone ? 560 : 880}
                destroyOnClose
            >
                {!editingZone && (
                    <Steps
                        current={currentStep}
                        style={{ margin: '8px 0 28px' }}
                        items={[
                            { title: t('Domain') },
                            { title: t('Select a plan') },
                            { title: t('Nameservers') }
                        ]}
                    />
                )}
                {renderStepContent()}
            </Modal>


            <DnsDnssec
                visible={!!dnssecZone}
                onClose={() => setDnssecZone(null)}
                zoneId={dnssecZone?.id}
                zoneName={dnssecZone?.display_zone || dnssecZone?.zone}
            />
        </div>
    );
};

export default DnsZones;
